import React, { useEffect, useState } from "react";

import {
  scrollPage,
  useGetFootprintingCropFootprintByPrimaryDataId,
  useGetFootprintingFarmFootprintByPrimaryDataId,
  useNavigate,
  useParams,
  useTranslation,
} from "utils";

import { FootprintingCropFormType, FootprintingFarmFormType } from "models";

import { Flex, Icon, Modal, PageSpin } from "components";

import { FootprintEditCropDetailsCard } from "pages/admin/verifications/components/footprinting/edit-details-cards/FootprintEditCropDetailsCard";
import { FootprintEditFarmDetailsCard } from "pages/admin/verifications/components/footprinting/edit-details-cards/FootprintEditFarmDetailsCard";
import { CropDryingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropDryingForm";
import { CropFertilizersForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFertilizersForm";
import { CropFieldSelectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFieldSelectionForm";
import { CropGeneralForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropGeneralForm";
import { CropIrrigationForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropIrrigationForm";
import { CropPlantProtectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropPlantProtectionForm";
import { CropTillageAndSeedingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTillageAndSeedingForm";
import { CropTransportForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTransportForm";
import { FarmElectricityForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmElectricityForm";
import { FarmFuelForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmFuelForm";
import { FarmRefrigerantsForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmRefrigerantsForm";
import { FarmSoilForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmSoilForm";
import { FarmTransportForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmTransportForm";
import { FarmWasteForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmWasteForm";
import { LandUseForm } from "pages/app/footprinting/components/formFlows/farmFlow/LandUseForm";

type FootprintType = "crop" | "farm";

interface FootprintEditDetailsPageParams {
  footprintPrimaryDataId?: string;
  footprintType?: FootprintType;
}

export const FootprintEditDetailsPage = () => {
  const { t } = useTranslation("footprinting");
  const navigate = useNavigate();
  const { footprintPrimaryDataId, footprintType } = useParams() as FootprintEditDetailsPageParams;
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editFarmModalForm, setFarmModalForm] = useState<FootprintingFarmFormType>(null);
  const [editCropModalForm, setCropModalForm] = useState<FootprintingCropFormType>(null);

  const farmFootprintQuery = useGetFootprintingFarmFootprintByPrimaryDataId(footprintPrimaryDataId ?? "", {
    enabled: footprintType === "farm",
  });
  const cropFootprintQuery = useGetFootprintingCropFootprintByPrimaryDataId(footprintPrimaryDataId ?? "", {
    enabled: footprintType === "crop",
  });

  const handleExit = () => navigate(`/verifications/farms/${farmFootprintQuery.data?.farmId ?? cropFootprintQuery.data?.farmId}/footprinting`);

  useEffect(() => {
    if (showEditModal) {
      setShowEditModal(true);
    }
  }, [showEditModal]);

  useEffect(() => {
    scrollPage();
  }, []);

  if (farmFootprintQuery.isLoading || cropFootprintQuery.isLoading) return <PageSpin />;

  const farmFootprintData = farmFootprintQuery.data;
  const cropFootprintData = cropFootprintQuery.data;

  return (
    <>
      <Flex justify="end" className="padding-200">
        <Icon iconName="tabler/x" onClick={handleExit} style={{ cursor: "pointer" }} />
      </Flex>
      {farmFootprintData && (
        <FootprintEditFarmDetailsCard
          farmFootprintData={farmFootprintData}
          isFarmFootprintDataLoading={farmFootprintQuery.isLoading}
          setShowEditModal={setShowEditModal}
          setFarmModalForm={setFarmModalForm}
        />
      )}
      {cropFootprintData && (
        <FootprintEditCropDetailsCard
          cropFootprintData={cropFootprintData}
          isCropFootprintDataLoading={cropFootprintQuery.isLoading}
          setShowEditModal={setShowEditModal}
          setCropModalForm={setCropModalForm}
        />
      )}
      <Modal
        showModal={showEditModal}
        title={t("footprinting:adminEditDetailsPage.modal.title", { type: footprintType === "farm" ? "farm" : "crop" })}
        footer={null}
        onCancel={() => setShowEditModal(false)}
        closable={true}
        destroyOnHidden={true}
        wrapClassName="modalconfirm-modal"
        content={
          <Flex className="footprinting-submission-modal">
            {footprintType === "farm" ? (
              <>
                {editFarmModalForm === "soil" && farmFootprintData?.farmId && (
                  <FarmSoilForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
                {editFarmModalForm === "electricity" && farmFootprintData?.farmId && (
                  <FarmElectricityForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
                {editFarmModalForm === "waste" && farmFootprintData?.farmId && (
                  <FarmWasteForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
                {editFarmModalForm === "fuel" && farmFootprintData?.farmId && (
                  <FarmFuelForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
                {editFarmModalForm === "cooling" && farmFootprintData?.farmId && (
                  <FarmRefrigerantsForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
                {editFarmModalForm === "transport" && farmFootprintData?.farmId && (
                  <FarmTransportForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
                {editFarmModalForm === "landuse" && farmFootprintData?.farmId && (
                  <LandUseForm primaryDataId={farmFootprintData?.footprint?.id} farmId={farmFootprintData?.farmId} year={farmFootprintData?.year} />
                )}
              </>
            ) : (
              <>
                {editCropModalForm === "general" && cropFootprintData?.cropId && (
                  <CropGeneralForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "fields" && cropFootprintData?.cropId && (
                  <CropFieldSelectionForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "tillageAndSeeding" && cropFootprintData?.cropId && (
                  <CropTillageAndSeedingForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "fertilizers" && cropFootprintData?.cropId && (
                  <CropFertilizersForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "plantProtection" && cropFootprintData?.cropId && (
                  <CropPlantProtectionForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "irrigation" && cropFootprintData?.cropId && (
                  <CropIrrigationForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "drying" && cropFootprintData?.cropId && (
                  <CropDryingForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
                {editCropModalForm === "transport" && cropFootprintData?.cropId && (
                  <CropTransportForm
                    primaryDataId={cropFootprintData?.footprint?.id}
                    farmId={cropFootprintData?.farmId}
                    cropId={cropFootprintData?.cropId}
                    year={cropFootprintData?.year}
                  />
                )}
              </>
            )}
          </Flex>
        }
      />
    </>
  );
};
